import type { AnalyzedItem } from '../lib/gemini';

export interface ProductGroup {
  indices: number[];
  confidence: number;
  reason: string;
  representativeItem: AnalyzedItem;
}

/**
 * Detects similar products that might be the same item photographed multiple times.
 * Uses instrument_id (exact match) as primary signal, then falls back to fuzzy matching.
 */
export function detectSimilarProducts(items: AnalyzedItem[]): ProductGroup[] {
  const groups: ProductGroup[] = [];
  const processed = new Set<number>();

  // Phase 1: Group by instrument_id (exact match — most reliable)
  const instrumentGroups = new Map<number, number[]>();
  items.forEach((item, idx) => {
    const instrumentId = item._backendData?.instrument_id;
    if (instrumentId != null) {
      const existing = instrumentGroups.get(instrumentId);
      if (existing) {
        existing.push(idx);
      } else {
        instrumentGroups.set(instrumentId, [idx]);
      }
    }
  });

  for (const [, indices] of instrumentGroups) {
    if (indices.length > 1) {
      // Multiple items share the same instrument_id — definitely the same product.
      indices.forEach(idx => processed.add(idx));
      const avgConfidence = indices.reduce((sum, idx) =>
        sum + (items[idx].confidenceScore || 0), 0) / indices.length;

      groups.push({
        indices,
        confidence: avgConfidence,
        reason: 'Same product identified by backend',
        representativeItem: items[indices[0]],
      });
    }
    // Singletons are NOT marked as processed — they fall through to Phase 2
    // fuzzy matching, which catches cases where Gemini gave different brands
    // for different angles of the same product.
  }

  // Phase 2: Fuzzy matching for remaining items (no instrument_id or unique instrument_id)
  for (let i = 0; i < items.length; i++) {
    if (processed.has(i)) continue;

    const currentItem = items[i];
    const similarIndices = [i];
    processed.add(i);

    for (let j = i + 1; j < items.length; j++) {
      if (processed.has(j)) continue;

      const otherItem = items[j];
      const similarity = calculateSimilarity(currentItem, otherItem);

      if (similarity.score >= 0.85) {
        similarIndices.push(j);
        processed.add(j);
      }
    }

    if (similarIndices.length > 1) {
      const avgConfidence = similarIndices.reduce((sum, idx) =>
        sum + (items[idx].confidenceScore || 0), 0) / similarIndices.length;

      groups.push({
        indices: similarIndices,
        confidence: avgConfidence,
        reason: getSimilarityReason(currentItem, items[similarIndices[1]]),
        representativeItem: currentItem,
      });
    }
  }

  return groups;
}

interface SimilarityResult {
  score: number;
  reasons: string[];
}

/**
 * Calculates similarity score between two items (0-1)
 */
function calculateSimilarity(item1: AnalyzedItem, item2: AnalyzedItem): SimilarityResult {
  let score = 0;
  const reasons: string[] = [];

  // Check if both items are in the same broad category
  const cat1 = normalizeString(item1.category || '');
  const cat2 = normalizeString(item2.category || '');
  const sameCategory = cat1 === cat2;

  // Check if both items share the same brand (even if one is "Unknown")
  const brand1 = normalizeString(item1.brand || '');
  const brand2 = normalizeString(item2.brand || '');
  const sameBrand = brand1 && brand2 && brand1 === brand2;
  const oneBrandUnknown = brand1 === 'unknown' || brand2 === 'unknown';

  // Check if items are related products (e.g., "One Connect Box" and "TV")
  const name1 = normalizeString(item1.name || '');
  const name2 = normalizeString(item2.name || '');
  const isRelatedProduct = checkRelatedProducts(name1, name2, item1.description, item2.description);

  // Same brand + same category = likely same product
  if (sameBrand && sameCategory) {
    score += 0.6;
    reasons.push('same brand and category');
  }

  // One brand unknown but same category + similar names
  if (oneBrandUnknown && sameCategory) {
    const nameSimilarity = calculateStringSimilarity(name1, name2);
    if (nameSimilarity > 0.5) {
      score += 0.5;
      reasons.push('same category, similar names');
    }
  }

  // Different brands but same category + very similar names — Gemini often
  // misidentifies the manufacturer from different angles (e.g. "Adidas" from
  // front logo vs "Nike" from back). High name similarity overrides brand mismatch.
  if (!sameBrand && !oneBrandUnknown && sameCategory) {
    const nameSimilarity = calculateStringSimilarity(name1, name2);
    if (nameSimilarity > 0.6) {
      score += 0.7;
      reasons.push('same category, very similar names despite different brands');
    }
  }

  // Related products (e.g., TV and One Connect Box) - only if SAME brand (not unknown)
  if (isRelatedProduct && sameBrand && !oneBrandUnknown) {
    score += 0.4;  // Reduced from 0.7
    reasons.push('related product components');
  }

  // Price range similarity (bonus points)
  if (item1.priceRange && item2.priceRange) {
    const price1 = (item1.priceRange.min + item1.priceRange.max) / 2;
    const price2 = (item2.priceRange.min + item2.priceRange.max) / 2;
    
    // For TVs, wide price range is normal
    const maxPrice = Math.max(price1, price2);
    const minPrice = Math.min(price1, price2);
    
    // If prices overlap or are within reasonable range for same product type
    if (maxPrice < minPrice * 3) {
      score += 0.2;
      reasons.push('compatible price range');
    }
  }

  // Description similarity
  if (item1.description && item2.description) {
    const descSimilarity = calculateStringSimilarity(
      normalizeString(item1.description),
      normalizeString(item2.description)
    );
    if (descSimilarity > 0.4) {
      score += 0.2 * descSimilarity;
      reasons.push('similar description');
    }
  }

  return { score: Math.min(score, 1), reasons };
}

/**
 * Check if two products are related components of the same item
 */
function checkRelatedProducts(name1: string, name2: string, desc1?: string, desc2?: string): boolean {
  // Common related product patterns
  const relatedPairs = [
    ['tv', 'television', 'oneconnect', 'oneconnectbox'],
    ['tv', 'television', 'monitor'],
    ['laptop', 'charger', 'adapter'],
    ['phone', 'case', 'charger'],
  ];

  const allTerms = [name1, name2, normalizeString(desc1 || ''), normalizeString(desc2 || '')];
  
  for (const pair of relatedPairs) {
    const matches = pair.filter(term => 
      allTerms.some(text => text.includes(term))
    );
    
    // If we find multiple related terms, they're likely the same product
    if (matches.length >= 2) {
      return true;
    }
  }

  return false;
}

/**
 * Get human-readable reason for similarity
 */
function getSimilarityReason(item1: AnalyzedItem, item2: AnalyzedItem): string {
  const reasons: string[] = [];

  if (item1.brand && item2.brand && normalizeString(item1.brand) === normalizeString(item2.brand)) {
    reasons.push('same brand');
  }

  if (item1.model && item2.model) {
    const similarity = calculateStringSimilarity(
      normalizeString(item1.model),
      normalizeString(item2.model)
    );
    if (similarity > 0.8) {
      reasons.push('same model');
    }
  }

  if (reasons.length === 0) {
    return 'Similar characteristics';
  }

  return reasons.join(' and ');
}

/**
 * Normalize string for comparison
 */
function normalizeString(str: string): string {
  return str.toLowerCase().trim().replace(/[^a-z0-9]/g, '');
}

/**
 * Calculate string similarity using Levenshtein distance
 */
function calculateStringSimilarity(str1: string, str2: string): number {
  const longer = str1.length > str2.length ? str1 : str2;
  const shorter = str1.length > str2.length ? str2 : str1;

  if (longer.length === 0) {
    return 1.0;
  }

  const editDistance = levenshteinDistance(longer, shorter);
  return (longer.length - editDistance) / longer.length;
}

/**
 * Calculate Levenshtein distance between two strings
 */
function levenshteinDistance(str1: string, str2: string): number {
  const matrix: number[][] = [];

  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  return matrix[str2.length][str1.length];
}
